/**
 * Login.
 *
 * Modos:
 *  - /login          → genérico (super-admin o fallback): logo "MOTOR POLICLÍNICO"
 *  - /login → con el logo y el nombre del policlínico
 *
 * Después de login, redirige al destino apropiado:
 */
import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import { useTenantBranding } from "../auth/TenantBrandingProvider";
import { MfaRequiredError } from "../api/auth";
import { ApiError } from "../api/client";
import { tenantLogoUrl } from "../api/tenant_branding";
import { partirNombre } from "../lib/marca";
import { Alert, Field, Input } from "../components/ui";

interface LocationState {
  from?: { pathname: string };
}

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { branding, isLoading: brandingLoading, isError: brandingError } = useTenantBranding();
  const from = (location.state as LocationState | null)?.from?.pathname ?? null;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Si la marca no carga, se avisa en vez de dejar la pantalla a medias
  if (brandingError) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-red-100 text-red-700 mb-4">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <h1 className="text-xl font-bold text-slate-900">Policlínico no encontrado</h1>
          <p className="text-sm text-slate-500 mt-2">
            La dirección que ingresaste no corresponde a ningún policlínico activo.
            Verifica el enlace o contacta a tu administrador.
          </p>
          <Link
            to="/login"
            className="inline-block mt-6 text-sm font-medium text-[color:var(--color-primary)] hover:text-[color:var(--color-primary)]"
          >
            Ir al login general →
          </Link>
        </div>
      </div>
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await login({
        email: email.trim(),
        password,
        mfa_code: mfaCode.trim() || undefined,
      });

      // Adónde va después de entrar: al sitio del que lo echaron si venía de
      // una ruta protegida, y si no, a su bandeja de trabajo — se entra a
      // trabajar, no a mirar gráficos. El dashboard sigue a un clic.
      let dest: string;
      if (from && from !== "/login" && !from.includes("/login")) {
        dest = from;
      } else {
        dest = "/mi-espacio";
      }
      navigate(dest, { replace: true });
    } catch (err) {
      if (err instanceof MfaRequiredError) {
        setMfaRequired(true);
        setError("Ingresa el código de tu app de autenticación.");
      } else if (err instanceof ApiError && err.status === 401) {
        setError("Credenciales inválidas. Verifica email y contraseña.");
      } else if (err instanceof ApiError && err.status === 422) {
        setError("Email o contraseña con formato inválido.");
      } else {
        setError("Error de conexión. Intenta de nuevo.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const showLogo = () => {
    if (branding?.has_logo) {
      return (
        // object-contain y no cover: el isotipo es más alto que ancho y
        // recortarlo le corta la cabeza a las figuras.
        <img
          src={tenantLogoUrl()}
          alt={branding.nombre_comercial}
          className="h-20 w-20 object-contain"
        />
      );
    }
    if (branding) {
      // Fallback: iniciales con color de marca
      const initials = branding.nombre_comercial.slice(0, 2).toUpperCase();
      return (
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-xl"
          style={{ backgroundColor: "var(--color-primary)" }}
        >
          {initials}
        </div>
      );
    }
    // Genérico
    return (
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[color:var(--color-primary)] text-white">
        <ShieldCheck className="w-6 h-6" />
      </div>
    );
  };

  // El archivo que sube el policlínico es solo el isotipo. El logotipo
  // completo lleva el nombre ENCIMA y el lema DEBAJO, cada uno en su color de
  // marca: así está impreso y así se arma aquí.
  const marca = () => {
    if (brandingLoading) {
      return (
        <div className="flex flex-col items-center gap-3">
          <div className="h-7 w-56 rounded bg-slate-200 animate-pulse" />
          <div className="h-20 w-20 rounded-2xl bg-slate-200 animate-pulse" />
          <div className="h-4 w-40 rounded bg-slate-200 animate-pulse" />
        </div>
      );
    }
    if (!branding) {
      return (
        <>
          <div className="inline-flex mb-4">{showLogo()}</div>
          {/* Sin marca todavía: ni el nombre interno del producto ni "SaaS".
              Esta instalación es de UN policlínico, y lo único honesto que se
              puede decir antes de que conteste el servidor es a qué entras. */}
          <h1 className="text-2xl font-bold text-slate-900">Policlínico</h1>
          <p className="text-sm text-slate-500 mt-1">Sistema de gestión clínica</p>
        </>
      );
    }
    const [tipo, propio] = partirNombre(branding.nombre_comercial);
    return (
      <>
        <h1 className="font-extrabold leading-[1.05] tracking-tight">
          {tipo && (
            <span
              className="block text-3xl"
              style={{ color: "var(--color-secondary)" }}
            >
              {tipo}
            </span>
          )}
          <span
            className={`block ${tipo ? "text-2xl" : "text-3xl"}`}
            style={{ color: "var(--color-primary)" }}
          >
            {propio}
          </span>
        </h1>
        <div className="inline-flex my-3">{showLogo()}</div>
        {branding.lema && (
          <p
            className="text-base font-semibold italic"
            style={{ color: "var(--color-secondary)" }}
          >
            {branding.lema}
          </p>
        )}
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">{marca()}</div>

        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
          {/* Nada de "bienvenido a X": el nombre está arriba, a tamaño de
              logotipo. Repetirlo aquí solo estorba al formulario. */}
          <h2 className="text-lg font-semibold text-slate-900 mb-6">Iniciar sesión</h2>

          {error && (
            <div className="mb-4">
              <Alert variant={mfaRequired ? "info" : "error"}>{error}</Alert>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <Field label="Email" htmlFor="email">
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu.correo@policlinico.pe"
                autoComplete="email"
                autoFocus
                required
              />
            </Field>

            <Field label="Contraseña" htmlFor="password">
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
              />
            </Field>

            {mfaRequired && (
              <Field
                label="Código de verificación (MFA)"
                htmlFor="mfa"
                hint="Código de 6 dígitos de tu app (Google Authenticator, 1Password)."
              >
                <Input
                  id="mfa"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  placeholder="123456"
                  inputMode="numeric"
                  maxLength={8}
                  autoComplete="one-time-code"
                  autoFocus
                />
              </Field>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed mt-2"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              {submitting && (
                <span className="inline-block w-4 h-4 border-2 border-current border-r-transparent rounded-full animate-spin" />
              )}
              {submitting ? "Verificando…" : "Iniciar sesión"}
            </button>

            <p className="mt-3 text-center text-xs text-slate-500">
              <Link
                to="/recuperar"
                className="font-medium text-[color:var(--color-link)] hover:underline"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </p>
          </form>

          {/* Esta pantalla es del personal, pero es la dirección que un
              paciente teclea cuando le dicen "entra a la web de la clínica".
              Sin esta puerta tendría que saberse /portal de memoria. */}
          {branding?.slug && (
            <div className="mt-6 border-t border-slate-100 pt-4 text-center">
              <Link
                to="/portal"
                className="text-sm font-medium text-[color:var(--color-link)] hover:underline"
              >
                ¿Eres paciente? Entra con tu DNI →
              </Link>
              <p className="mt-1 text-xs text-slate-500">
                Consulta tus citas, recetas y resultados.
              </p>
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-slate-100 text-center">
            <Link
              to="/soporte"
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              ¿Problemas para acceder? Contacta a soporte
            </Link>
          </div>
        </div>

        {/* Con branding el software es del cliente y su logotipo ya está
            arriba: repetir el nombre al pie no añade nada. El crédito de la
            plataforma solo aparece en el login genérico. */}
        {!branding && (
          <p className="text-center text-xs text-slate-400 mt-6">
            MOTOR POLICLÍNICO · ERP para policlínicos en Perú
          </p>
        )}
      </div>
    </div>
  );
}

// Workaround: Button no acepta `style` dinámico con CSS var, así que en este archivo
// usamos un button directamente para el color primario. Para futuras pantallas
// podemos crear `<Button variant="brand">` que use var(--color-primary).
